export type LibrarySyncAutomationId =
  | 'albumSaveCascade'
  | 'albumUnsaveCascade'
  | 'recordingSaveCascade'
  | 'recordingUnsaveTracks'
  | 'recordingSaveTracks'
  | 'albumSaveRecordings'
  | 'albumUnsaveRecordings'
  | 'workUnsaveRecordings';

export type LibrarySyncOneOffId =
  | 'syncAlbumTracks'
  | 'syncAlbumRecordings'
  | 'syncRecordingWorks'
  | 'syncTrackRecordings';

export type LibrarySyncAutomationStatus = {
  enabled: boolean;
  updatedAt: string | null;
};

export type LibrarySyncOneOffStatus = {
  lastRunAt: string | null;
  lastStatus: 'success' | 'failed' | null;
};

export type RecordingsAndWorksSyncJobStatus = {
  jobId: string;
  spotifyUserId: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  phase: 'recordings' | 'album_recordings' | 'track_recordings' | 'works' | 'completed' | 'error';
  startedAt: string | null;
  finishedAt: string | null;
  lastUpdate: string | null;
  error?: string | null;
  recordings?: {
    totalAlbums: number;
    albumsProcessed: number;
    totalTracksInspected: number;
    totalRecordings: number;
    saved: number;
    skipped: number;
  };
  albumRecordings?: {
    totalAlbums: number;
    albumsProcessed: number;
    totalTracksInspected: number;
    totalRecordings: number;
    saved: number;
    skipped: number;
  };
  trackRecordings?: {
    totalTracks: number;
    tracksProcessed: number;
    totalRecordings: number;
    saved: number;
    skipped: number;
  };
  works?: {
    totalFavorites: number;
    resolved: number;
    worksEnsured: number;
    skipped: number;
    skippedAmbiguous: number;
    skippedNoMatch: number;
    failedWorkUpserts: number;
  };
};

export interface LibrarySyncSettingsValue {
  version: number;
  automations: Record<LibrarySyncAutomationId, LibrarySyncAutomationStatus>;
  oneOffs: Record<LibrarySyncOneOffId, LibrarySyncOneOffStatus>;
}

export interface LibrarySyncSettingsEnvelope {
  key: 'librarySync';
  value: LibrarySyncSettingsValue;
}

export type LibrarySyncSettingsUpdate = {
  version: number;
  automations?: Partial<Record<LibrarySyncAutomationId, Partial<LibrarySyncAutomationStatus>>>;
  oneOffs?: Partial<Record<LibrarySyncOneOffId, Partial<LibrarySyncOneOffStatus>>>;
};

export const LIBRARY_SYNC_SETTINGS_VERSION = 1;

export const createDefaultLibrarySyncSettings = (
  now: Date = new Date()
): LibrarySyncSettingsValue => {
  const isoNow = now.toISOString();
  return {
    version: LIBRARY_SYNC_SETTINGS_VERSION,
    automations: {
      albumSaveCascade: {
        enabled: false,
        updatedAt: isoNow,
      },
      albumUnsaveCascade: {
        enabled: false,
        updatedAt: isoNow,
      },
      recordingSaveCascade: {
        enabled: true,
        updatedAt: isoNow,
      },
      recordingUnsaveTracks: {
        enabled: false,
        updatedAt: isoNow,
      },
      recordingSaveTracks: {
        enabled: false,
        updatedAt: isoNow,
      },
      albumSaveRecordings: {
        enabled: true,
        updatedAt: isoNow,
      },
      albumUnsaveRecordings: {
        enabled: true,
        updatedAt: isoNow,
      },
      workUnsaveRecordings: {
        enabled: true,
        updatedAt: isoNow,
      },
    },
    oneOffs: {
      syncAlbumTracks: {
        lastRunAt: null,
        lastStatus: null,
      },
      syncAlbumRecordings: {
        lastRunAt: null,
        lastStatus: null,
      },
      syncRecordingWorks: {
        lastRunAt: null,
        lastStatus: null,
      },
      syncTrackRecordings: {
        lastRunAt: null,
        lastStatus: null,
      },
    },
  };
};

export interface SavedAlbumTracksSyncResult {
  totalAlbums: number;
  totalTracks: number;
  saved: number;
  skipped: number;
}

export interface SavedAlbumRecordingsSyncResult {
  totalAlbums: number;
  totalTracksInspected: number;
  totalRecordings: number;
  saved: number;
  skipped: number;
}

export interface RecordingWorksSyncResult {
  totalFavorites: number;
  totalResolved: number;
  worksEnsured: number;
  skipped: number;
  skippedAmbiguous: number;
  skippedNoMatch: number;
  failedWorkUpserts: number;
}
